import { API_PREFIX, CSRF_HEADER, errorResponseSchema } from '@soe/protocol';
import { env } from '../config/env.js';

/** A failed API call with a stable code the UI can branch on. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fieldErrors: Record<string, string> = {},
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken: string | null = null;

/** Held in memory only, never in localStorage/sessionStorage. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/** Notified whenever the server says the session is gone (revoked or missing). */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export async function api<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  options: { signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers[CSRF_HEADER] = csrfToken;

  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_ORIGIN}${API_PREFIX}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      cache: 'no-store',
      body: body === undefined ? null : JSON.stringify(body),
      signal: options.signal ?? null,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(
      0,
      'NETWORK',
      'Can’t reach the server. Check your connection and try again.',
    );
  }

  if (response.status === 204) return undefined as T;
  const json: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorResponseSchema.safeParse(json);
    // A 5xx without our JSON error body came from a proxy or gateway, not the API:
    // the API itself is down or unreachable.
    const error = parsed.success
      ? parsed.data.error
      : {
          code: response.status >= 500 ? 'NETWORK' : 'INTERNAL',
          message:
            response.status >= 500
              ? 'Can’t reach the game server. It may be down. Try again in a moment.'
              : 'Something went wrong.',
          issues: undefined,
          retryAfterSeconds: undefined,
        };
    if (response.status === 401) {
      for (const listener of unauthorizedListeners) listener();
    }
    const fieldErrors: Record<string, string> = {};
    for (const issue of error.issues ?? []) fieldErrors[issue.path] ??= issue.message;
    throw new ApiError(
      response.status,
      error.code,
      error.message,
      fieldErrors,
      error.retryAfterSeconds,
    );
  }
  return json as T;
}
